import React, { useEffect, useState } from 'react';
import { Box, Chip, Skeleton, Tooltip, Typography } from '@mui/material';
import { ImageNotSupportedOutlined as NoImageIcon } from '@mui/icons-material';
import { ImageSize, NcelsStatus, NktStatus, isCancelled, nomenclatureApi } from '../nomenclatureApi';
import { NCELS_COLORS, NCELS_HINTS, NCELS_LABELS, NKT_COLORS, NKT_HINTS, NKT_LABELS } from '../nomenclatureCalc';
import { EMPTY_TEXT, emptySx, labelSx, smallChipSx, valueSx } from '../nomenclatureStyles';

/** Изображение из кеша :3004: фото НКТ (имя файла) или макет упаковки РУ (id) */
export interface ImageRef { kind: 'nkt' | 'box'; id: string | number }

interface AuthImageProps {
  image: ImageRef | null;
  size: ImageSize;
  width: number | string;
  height: number | string;
  alt: string;
  /** Клик - открыть крупно */
  onClick?: () => void;
}

/**
 * Картинка грузится запросом с сессией в заголовке и показывается через object URL:
 * токен не попадает в адрес изображения, а браузер берёт повторные запросы из своего кеша (max-age сутки)
 */
export const AuthImage: React.FC<AuthImageProps> = ({ image, size, width, height, alt, onClick }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const kind = image ? image.kind : null;
  const id = image ? image.id : null;

  useEffect(() => {
    setUrl(null);
    setFailed(false);
    if (!kind || id === null) return undefined;
    const controller = new AbortController();
    let objectUrl: string | null = null;
    nomenclatureApi.image(kind, id, size, controller.signal)
      .then(blob => {
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(err => {
        if (!isCancelled(err)) setFailed(true);
      });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [kind, id, size]);

  const boxSx = {
    width, height, flexShrink: 0, borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'
  };

  if (!image || failed) {
    return (
      <Box sx={{ ...boxSx, bgcolor: '#f8fafc', color: '#cbd5e1' }} title={failed ? 'Изображение недоступно в источнике' : 'Изображения нет'}>
        <NoImageIcon sx={{ fontSize: typeof height === 'number' ? Math.min(28, height * 0.5) : 28 }} />
      </Box>
    );
  }
  if (!url) return <Skeleton variant="rounded" width={width} height={height} sx={{ flexShrink: 0 }} />;
  return (
    <Box sx={{ ...boxSx, cursor: onClick ? 'zoom-in' : 'default' }} onClick={onClick}>
      <Box component="img" src={url} alt={alt} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }} />
    </Box>
  );
};

/** Изображение строки списка: фото НКТ, если нет - макет упаковки из РУ */
export const imageOf = (photo: string | null, boxId: number | null): ImageRef | null => {
  if (photo) return { kind: 'nkt', id: photo };
  if (boxId) return { kind: 'box', id: boxId };
  return null;
};

interface StatusChipProps { source: 'nkt'; status: NktStatus; hint?: string }
interface NcelsChipProps { source: 'ncels'; status: NcelsStatus; hint?: string }

/** Состояние сверки с источником; подсказка объясняет, что значит статус */
export const StatusChip: React.FC<StatusChipProps | NcelsChipProps> = props => {
  const { label, color, hint } = props.source === 'nkt'
    ? { label: NKT_LABELS[props.status], color: NKT_COLORS[props.status], hint: NKT_HINTS[props.status] }
    : { label: NCELS_LABELS[props.status], color: NCELS_COLORS[props.status], hint: NCELS_HINTS[props.status] };
  return (
    <Tooltip title={props.hint ? `${hint}: ${props.hint}` : hint} arrow>
      <Chip size="small" label={label} sx={{ ...smallChipSx, color: color.color, bgcolor: color.bg, maxWidth: '100%' }} />
    </Tooltip>
  );
};

/** Значение ячейки в одну строку; пустое - «не указано» */
export const Value: React.FC<{ text: string | null | undefined; title?: boolean }> = ({ text, title }) => (
  text
    ? <Typography title={title ? text : undefined} sx={{ fontSize: 13, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</Typography>
    : <Typography sx={emptySx}>{EMPTY_TEXT}</Typography>
);

/** Поле карточки: подпись и значение; пустые поля не выводятся */
export const Field: React.FC<{ label: string; value: React.ReactNode; wide?: boolean; mono?: boolean }> = ({ label, value, wide, mono }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <Box sx={{ minWidth: 0, gridColumn: wide ? '1 / -1' : undefined }}>
      <Typography sx={labelSx}>{label}</Typography>
      <Typography component="div" sx={{ ...valueSx, fontFamily: mono ? 'monospace' : undefined }}>{value}</Typography>
    </Box>
  );
};

/** Сетка полей карточки: 3 колонки на широком экране */
export const FieldGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
    {children}
  </Box>
);
